import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import type { IndustryDto } from '@/services/ws-crm/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { formatIndustryOptionLabel } from '../helpers';
import { useIndustriesOptions } from '../hooks/useIndustriesOptions';

interface IndustryFilterProps {
  value: Option<IndustryDto> | null;
  onChange: (value: SingleValue<Option<IndustryDto>>) => void;
  label?: string;
}

export const IndustryFilter: React.FC<IndustryFilterProps> = React.memo(function IndustryFilter({ value, onChange, label }) {
  const [t] = useTranslation(['backend', 'common']);
  const [, { language }] = useTranslation(['backend']);
  const { options, pending: loading } = useIndustriesOptions();

  const handleChange = useCallbackRef((newValue: SingleValue<Option<IndustryDto>>) => {
    onChange(newValue);
  });

  const translatedFilter = React.useMemo(
    () =>
      value
        ? {
            ...value,
            label: formatIndustryOptionLabel(value.value, language),
          }
        : null,
    [value, language],
  );

  return (
    <Filter<IndustryDto>
      label={label || t('common:INDUSTRY')}
      loading={loading}
      selected={translatedFilter}
      onChange={handleChange}
      options={options}
      dataTestId='industry-dropdown'
    />
  );
});
